import { useMemo, useEffect } from 'react';
import { useRevitParametersStore } from '../stores/useRevitParametersStore';
import type { DropdownOption } from '../ui/forms/SearchableDropdown';
import { useActiveDocumentInfoContext } from './ActiveDocumentProvider';
import { normalizeMappingProjectKey } from '../lib/mappingProjectIdentity';
import { formatParameterSource } from '../lib/revit/parameterSource';
import type { ParameterSource } from '../api/grpc/gen/family_service_pb';

export interface RevitParameterOptionSource {
  readonly name: string;
  readonly label: string;
  readonly parameterType: 'Type' | 'Instance';
  readonly group: string;
  readonly dataType?: string;
  readonly source?: ParameterSource;
}

export type RevitParameterType = RevitParameterOptionSource['parameterType'];

interface RevitParameterOptionsConfig {
  readonly enabled?: boolean;
  readonly preferredParameterType?: RevitParameterType;
}

function normalizeOptionName(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeOptionDataType(value: string | undefined): string | null {
  const normalized = value?.trim().toUpperCase();
  return normalized || null;
}

export function buildRevitParameterOptions(
  parameters: readonly RevitParameterOptionSource[],
  preferredParameterType?: RevitParameterType
): DropdownOption[] {
  const optionsByOccurrence = new Map<string, DropdownOption>();
  const orderedParameters = preferredParameterType
    ? [
        ...parameters.filter((parameter) => parameter.parameterType === preferredParameterType),
        ...parameters.filter((parameter) => parameter.parameterType !== preferredParameterType),
      ]
    : parameters;

  orderedParameters.forEach((parameter) => {
    const key = `${normalizeOptionName(parameter.name)}::${parameter.parameterType}`;
    const existing = optionsByOccurrence.get(key);
    const dataType = normalizeOptionDataType(parameter.dataType);
    if (!existing) {
      const sourceText = formatParameterSource(parameter.source);
      optionsByOccurrence.set(key, {
        value: parameter.name,
        label: parameter.label,
        occurrenceKey: key,
        tooltip: `${parameter.parameterType} parameter: ${parameter.name}${sourceText ? ` · ${sourceText}` : ''}`,
        group: parameter.group,
        ...(dataType ? { dataType } : {}),
      });
      return;
    }

    if (normalizeOptionDataType(existing.dataType) !== dataType) {
      const merged = { ...existing };
      delete merged.dataType;
      optionsByOccurrence.set(key, merged);
    }
  });

  return Array.from(optionsByOccurrence.values());
}

/**
 * Custom hook for getting Revit parameter options for dropdowns
 * Separates data fetching, transformation, and UI concerns
 *
 * @returns Dropdown options with proper grouping by Type/Instance parameters
 */
export function useRevitParameterOptions(config?: RevitParameterOptionsConfig): {
  options: DropdownOption[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
} {
  const enabled = config?.enabled ?? true;
  // Data layer - fetch from store
  const parameters = useRevitParametersStore((state) => state.parameters);
  const fetchParameters = useRevitParametersStore((state) => state.fetchParameters);
  const isLoading = useRevitParametersStore((state) => state.isLoading);
  const isLoaded = useRevitParametersStore((state) => state.isLoaded);
  const error = useRevitParametersStore((state) => state.error);
  const loadedProjectKey = useRevitParametersStore((state) => state.projectKey);
  const loadedModelVersion = useRevitParametersStore((state) => state.modelVersion);
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const activeProjectKey = useMemo(
    () => normalizeMappingProjectKey(activeDocumentInfo.projectKey),
    [activeDocumentInfo.projectKey]
  );
  const storeMatchesActiveDocument =
    loadedProjectKey === activeProjectKey &&
    loadedModelVersion === activeDocumentInfo.modelVersion;

  // Fetch parameters on mount (side effect)
  useEffect(() => {
    const shouldFetch =
      enabled &&
      Boolean(activeProjectKey) &&
      (!storeMatchesActiveDocument || (!isLoading && !isLoaded && error === null));

    if (shouldFetch) {
      fetchParameters({
        projectKey: activeProjectKey,
        modelVersion: activeDocumentInfo.modelVersion,
      });
    }
  }, [
    activeDocumentInfo.modelVersion,
    activeProjectKey,
    enabled,
    error,
    isLoaded,
    isLoading,
    fetchParameters,
    loadedModelVersion,
    loadedProjectKey,
    storeMatchesActiveDocument,
  ]);

  // Transformation layer - convert to dropdown options
  const options = useMemo(() => {
    if (!storeMatchesActiveDocument || parameters.length === 0) {
      return [];
    }

    return buildRevitParameterOptions(parameters, config?.preferredParameterType);
  }, [config?.preferredParameterType, parameters, storeMatchesActiveDocument]);

  return {
    options,
    isLoading: storeMatchesActiveDocument
      ? isLoading
      : enabled && Boolean(activeProjectKey),
    isLoaded: storeMatchesActiveDocument && isLoaded,
    error: storeMatchesActiveDocument ? error : null,
  };
}
